import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSafeTwilioConversations, AppMessage } from '../../contexts/TwilioConversationsContext';
import { useAuth } from '../../context/AuthContext';
import { formatDistanceToNow, format } from 'date-fns';
import { enUS, fr, es, de, it as itLocale, type Locale } from 'date-fns/locale';
import { ArrowLeft, Send, ExternalLink } from 'lucide-react';
import './MessagesPopover.css';

const DATE_FNS_LOCALES: Record<string, Locale> = {
    fr,
    en: enUS,
    es,
    de,
    it: itLocale,
};

interface MessagesPopoverProps {
    onClose: () => void;
}

const MessagesPopover: React.FC<MessagesPopoverProps> = ({ onClose }) => {
    const { t, i18n } = useTranslation('messaging');
    const dateLocale = useMemo(
        () => DATE_FNS_LOCALES[i18n.language.split('-')[0]?.toLowerCase() ?? 'fr'] ?? fr,
        [i18n.language]
    );
    const {
        conversations,
        loading,
        messages,
        fetchMessages,
        sendMessage,
        markConversationAsRead
    } = useSafeTwilioConversations();

    const { user } = useAuth();
    const navigate = useNavigate();
    const [selectedSid, setSelectedSid] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState('');
    const [isSending, setIsSending] = useState(false);
    const messagesEndRef = useRef<HTMLDivElement>(null);

    const proIdentity = user?.pro_id ? `pro_${user.pro_id}` : '';

    const selectedConversation = conversations.find(c => c.conversation_sid === selectedSid);
    const conversationMessages: AppMessage[] = selectedSid
        ? (messages as Record<string, AppMessage[]>)[selectedSid] || []
        : [];

    useEffect(() => {
        if (selectedSid) {
            fetchMessages(selectedSid);
            markConversationAsRead(selectedSid);
        }
    }, [selectedSid, fetchMessages, markConversationAsRead]);

    useEffect(() => {
        if (selectedSid) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [conversationMessages, selectedSid]);

    const handleConversationClick = async (conversationSid: string) => {
        await markConversationAsRead(conversationSid);
        setSelectedSid(conversationSid);
    };

    const handleViewAll = () => {
        const url = selectedSid ? `/app/messages?sid=${selectedSid}` : '/app/messages';
        navigate(url);
        onClose();
    };

    const handleBack = () => {
        setSelectedSid(null);
    };

    const handleSend = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!inputValue.trim() || isSending || !selectedSid) return;

        setIsSending(true);
        try {
            // Utiliser un ID temporaire pour déclencher une mise à jour optimiste dans la liste
            const tempId = `popover_temp_${Date.now()}`;
            await sendMessage(selectedSid, inputValue, undefined, tempId);
            setInputValue('');
        } catch (error) {
            console.error('Failed to send message:', error);
        } finally {
            setIsSending(false);
        }
    };

    // Close on escape key
    useEffect(() => {
        const handleEsc = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                onClose();
            }
        };
        window.addEventListener('keydown', handleEsc);
        return () => window.removeEventListener('keydown', handleEsc);
    }, [onClose]);

    return (
        <div className="messages-popover" onClick={(e) => e.stopPropagation()}>
            <div className="messages-popover-header">
                {selectedSid ? (
                    <div className="popover-header-chat">
                        <button className="back-btn" onClick={handleBack}>
                            <ArrowLeft size={18} />
                        </button>
                        <div className="header-user-info">
                            <img
                                src={selectedConversation?.participant_avatar || 'https://rintintin-bucket.s3.eu-west-3.amazonaws.com/default-profile.jpg'}
                                alt=""
                            />
                            <span className="user-name">{selectedConversation?.friendly_name}</span>
                        </div>
                        <button className="full-view-btn" onClick={handleViewAll} title={t('messagesPopover.fullscreen')}>
                            <ExternalLink size={16} />
                        </button>
                    </div>
                ) : (
                    <>
                        <h3>{t('messagesPopover.title')}</h3>
                        <button className="view-all-btn" onClick={handleViewAll}>
                            {t('messagesPopover.viewAll')}
                        </button>
                    </>
                )}
            </div>

            <div className={`messages-popover-content ${selectedSid ? 'chat-view' : 'list-view'}`}>
                {selectedSid ? (
                    <div className="popover-chat-messages">
                        {conversationMessages.length === 0 && loading ? (
                            <div className="messages-popover-loading">
                                <div className="spinner"></div>
                            </div>
                        ) : (
                            <>
                                {conversationMessages.map((msg: AppMessage, idx: number) => (
                                    <div key={msg.message_sid || idx} className={`popover-msg ${msg.sender_identity.startsWith('pro_') ? 'sent' : 'received'}`}>
                                        <div className="popover-msg-bubble">
                                            {msg.message_body || msg.body}
                                        </div>
                                        <span className="popover-msg-time">{format(new Date(msg.timestamp), 'HH:mm')}</span>
                                    </div>
                                ))}
                                <div ref={messagesEndRef} />
                            </>
                        )}
                    </div>
                ) : (
                    <>
                        {loading && conversations.length === 0 ? (
                            <div className="messages-popover-loading">
                                <div className="spinner"></div>
                                <span>{t('messagesPopover.loading')}</span>
                            </div>
                        ) : conversations.length === 0 ? (
                            <div className="messages-popover-empty">
                                <p>{t('messagesPopover.noConversations')}</p>
                            </div>
                        ) : (
                            <div className="messages-list">
                                {conversations.slice(0, 10).map((conv) => (
                                    <div
                                        key={conv.conversation_sid}
                                        className={`message-item ${conv.hasUnreadMessages ? 'unread' : ''}`}
                                        onClick={() => handleConversationClick(conv.conversation_sid)}
                                    >
                                        <div className="message-item-avatar">
                                            <img
                                                src={conv.participant_avatar || 'https://rintintin-bucket.s3.eu-west-3.amazonaws.com/default-profile.jpg'}
                                                alt={conv.friendly_name}
                                            />
                                            {conv.hasUnreadMessages && <span className="unread-dot"></span>}
                                        </div>
                                        <div className="message-item-info">
                                            <div className="message-item-top">
                                                <span className="message-item-name">{conv.friendly_name}</span>
                                                <span className="message-item-time">
                                                    {conv.last_message_time ?
                                                        formatDistanceToNow(new Date(conv.last_message_time), { addSuffix: true, locale: dateLocale })
                                                        : ''}
                                                </span>
                                            </div>
                                            <div className="message-item-bottom">
                                                <p className="message-item-last">
                                                    {conv.last_message_author === proIdentity && <span className="last-msg-you">{t('messagesPopover.youPrefix')}</span>}
                                                    {conv.last_message || t('messagesPopover.noMessage')}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </>
                )}
            </div>

            {selectedSid ? (
                <form className="popover-chat-input" onSubmit={handleSend}>
                    <input
                        type="text"
                        placeholder={t('messagesPopover.inputPlaceholder')}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        autoFocus
                    />
                    <button type="submit" disabled={!inputValue.trim() || isSending}>
                        <Send size={18} />
                    </button>
                </form>
            ) : (
                <div className="messages-popover-footer" onClick={handleViewAll}>
                    {t('messagesPopover.viewPreviousMessages')}
                </div>
            )}
        </div>
    );
};

export default MessagesPopover;
